import React, { Suspense, lazy, useContext, useEffect, useState } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { Container, Row, Col, Spinner, Button, Modal, Table } from 'react-bootstrap';
import { RestaurantMenuProvider, RestaurantMenuContext } from '../Components/RestaurantMenuContext';
import { withNamespaces } from "react-i18next"
import { connect } from "react-redux"

// Lazy load components
const CategoryList = lazy(() => import('../Components/CategoryList'));
const ProductList = lazy(() => import('../Components/ProductList'));
const SearchBar = lazy(() => import('../Components/SearchBar'));
const CartSection = lazy(() => import('../Components/CartSection'));
const OrderSuccessScreen = lazy(() => import('../Components/OrderSuccessScreen'));
const AddonGroupModal = lazy(() => import('../Components/AddonGroupModal'));
const VariantModal = lazy(() => import('../Components/VariantModal'));

// Loading fallback for lazy loaded components
const LazyLoadingFallback = () => (
  <div className="text-center p-4">
    <Spinner animation="border" role="status">
      <span className="visually-hidden">Loading...</span>
    </Spinner>
  </div>
);

// Table Information Component
const TableInfo = () => {
    const { tableInfo } = useContext(RestaurantMenuContext);
    // console.log("tableInfo===",tableInfo)
    // Only show if table information is available
    if (!tableInfo) {
        return null;
    }

    return (
        <div className="bg-white px-3 py-2 border-bottom">
            <div className="d-flex align-items-center">
                <span className="text-muted me-2">Table</span>
                <span className="fw-bold">{tableInfo.table_number}</span>
            </div>
        </div>
    );
};

// Back Button Component
const BackButton = () => {
    const history = useHistory();
    const { lng } = useParams();

    const handleBackClick = () => {
        history.push(`/${lng}/restaurant/menu`); 
    };

    return (
        <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={handleBackClick}
            className="me-2"
            title="Back to Restaurant Menu"
        >
            <i className="fa fa-arrow-left">Back</i>
        </Button>
    );
};

// History Icon Component
const HistoryIcon = () => {
    const { fetchOrderHistory, setShowOrderHistory } = useContext(RestaurantMenuContext);

    const handleHistoryClick = () => {
        fetchOrderHistory();
        setShowOrderHistory(true);
    };

    return (
        <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={handleHistoryClick}
            className="me-2"
            title="Order History"
        >
            <i className="fa fa-history"></i>
        </Button>
    );
};

// Order History Modal Component
const OrderHistoryModal = () => {
    const { 
        showOrderHistory, 
        setShowOrderHistory, 
        orderHistory, 
        loadingHistory 
    } = useContext(RestaurantMenuContext);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return dateString;
        }
    };

    const formatAmount = (amount) => {
        return amount ? `₹${parseFloat(amount).toFixed(2)}` : '₹0.00';
    };

    return (
        <Modal 
            show={showOrderHistory} 
            onHide={() => setShowOrderHistory(false)}
            size="lg"
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>Order History</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loadingHistory ? (
                    <div className="text-center p-4">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                        <p className="mt-2">Loading order history...</p>
                    </div>
                ) : orderHistory.length === 0 ? (
                    <div className="text-center p-4">
                        <i className="fa fa-inbox fa-3x text-muted mb-3"></i>
                        <p className="text-muted">No order history found</p>
                    </div>
                ) : (
                    <div className="order-history-list">
                        {orderHistory.map((order, index) => (
                            <div key={order.id || index} className="order-card mb-3 border rounded p-3 bg-white">
                                {/* Order Header */}
                                <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                                    <div>
                                        <h6 className="mb-1">
                                            <strong>#{order.order_number}</strong>
                                        </h6>
                                        <small className="text-muted">
                                            {formatDate(order.created_at)} | {order.customer_name || order.customer_phone || 'Guest'}
                                        </small>
                                    </div>
                                    <div className="text-end">
                                        <div className="h6 mb-1">{formatAmount(order.total_order_amount)}</div>
                                        <span className={`badge ${
                                            order.status_data?.value_constant === 'CUSTOMER_ORDER' ? 'bg-primary' :
                                            order.status_data?.value_constant === 'CANCELLED' ? 'bg-danger' :
                                            'bg-secondary'
                                        }`}>
                                            {order.status_data?.label || 'Unknown'}
                                        </span>
                                    </div>
                                </div>
                                
                                {/* Order Products */}
                                <div className="order-products">
                                    <div className="fw-bold mb-2 text-muted small">Items</div>
                                    {order.order_products && order.order_products.length > 0 ? (
                                        order.order_products.map((product, productIndex) => (
                                            <div key={product.id || productIndex} className="d-flex justify-content-between align-items-center py-1">
                                                <div className="flex-grow-1">
                                                    <span className="product-name">{product.name}</span>
                                                </div>
                                                <div className="text-end">
                                                    <span className="badge bg-light text-dark">Qty: {parseFloat(product.quantity).toFixed(0)}</span>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-muted small">No product details available</div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowOrderHistory(false)}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// Menu content component that handles conditional rendering
const MenuContent = ({ currentStore }) => {
    const { orderSuccess } = useContext(RestaurantMenuContext);
    const { storeData } = useContext(RestaurantMenuContext);

    if (orderSuccess) {
        return (
            <Suspense fallback={<LazyLoadingFallback />}>
                <OrderSuccessScreen currentStoreDetails = {currentStore}/>
            </Suspense>
        );
    }
    
    // Render AddonGroupModal alongside regular content

    return (
        <>
            <div className="d-flex flex-column vh-100 bg-light">
                {/* Header - Contains store info and cart button */}
                <div className="bg-white d-flex justify-content-between align-items-center p-3 shadow-sm sticky-top flex-shrink-0">
                    <div className="d-flex align-items-center">
                        <span className="fw-bold">
                            {/* {currentStore?.name || 'Restaurant Menu'} */}
                            {storeData?.name || 'Restaurant Menu'}
                        </span>
                    </div>
                    <div className="d-flex align-items-center">
                        <BackButton />
                        <HistoryIcon />
                        <Suspense fallback={<LazyLoadingFallback />}>
                            <CartSection />
                        </Suspense>
                    </div>
                </div>
                
                {/* Search Bar */}
                <div className="flex-shrink-0">
                    <Suspense fallback={<LazyLoadingFallback />}>
                        <SearchBar />
                    </Suspense>
                </div>
                
                {/* Categories */}
                <div className="menuBox desktopCategory list active flex-shrink-0" id="categoryList">
                    <Suspense fallback={<LazyLoadingFallback />}>
                        <CategoryList />
                    </Suspense>
                </div>

                {/* Table Information */}
                <TableInfo />
                
                {/* Menu Content - Scrollable Area */}
                <div className="flex-grow-1 overflow-auto">
                    <Container fluid className="py-3 h-100">
                        <Row className="h-100">
                            <Col xs={12}>
                                <Suspense fallback={<LazyLoadingFallback />}>
                                    <ProductList />
                                </Suspense>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </div>
            
            {/* Product Customize Modal */}
            <Suspense fallback={<LazyLoadingFallback />}>
                <AddonGroupModal t={null} />
            </Suspense>
            
            {/* Product Variant Modal */}
            <Suspense fallback={<LazyLoadingFallback />}>
                <VariantModal t={null} />
            </Suspense>
            
            {/* Order History Modal */}
            <OrderHistoryModal />
        </>
    );
};

const SimpleRestaurantMenu = ({ currentStore }) => {
    const { menuSlug } = useParams();
    // console.log('SimpleRestaurantMenu loaded with slug:', menuSlug);
    
    return (
        <RestaurantMenuProvider menuSlug={menuSlug}>
            <MenuContent currentStore={currentStore} />
        </RestaurantMenuProvider>
    );
};

// export default SimpleRestaurantMenu;

const mapStateToProps = (state) => ({
  currentStore: state.StoreReducer.currentStore
});

export default connect(mapStateToProps)(withNamespaces()(SimpleRestaurantMenu))
